// covered-by: frontends/carrie/src/routes/tickets/__tests__/ticketShellLifecycle.test.ts
//
// ticketRoute (/tickets/:id/view) and ticketCommentRoute
// (/tickets/:id/:boardId/:commentId) are sibling shells that share TicketView.
// Ask Puntt keeps shareable comment URLs by soft-pathing between siblings
// without tearing down the review shell. Classic tickets must do the same:
// leaving view↔comment for the SAME ticket must NOT resetTicket / drop the
// comments query (that remounts tldraw mid-click → Redux #3).

import { type QueryClient } from "@tanstack/react-query";

import { isStillOnSameTicketShell } from "./ticketCommentPaths";

import { BASEPATH } from "../../config/env";
import { resetTicket, setLoading } from "../../utils/slices/ticket";
import { store } from "../../utils/store";

/** Must match `query` in ticket/route.ts (comments list key). */
const TICKET_COMMENTS_QUERY = "all" as const;

/** Bumped on every leave so superseded deferred cleanups no-op. */
let leaveEpoch = 0;

export function readBrowserPathname(): string {
  if (typeof window === "undefined") return "";
  let path = window.location.pathname ?? "";
  // Epic deploys serve carrie under VITE_BASEPATH (e.g. /ticket-comment-urls).
  const base = BASEPATH === "/" ? "" : BASEPATH.replace(/\/$/, "");
  if (base && (path === base || path.startsWith(`${base}/`))) {
    path = path.slice(base.length) || "/";
  }
  return path;
}

function dropTicketCommentsQuery(
  queryClient: QueryClient,
  ticketId: string,
): void {
  queryClient.removeQueries({
    queryKey: ["ticket-comments", ticketId, TICKET_COMMENTS_QUERY],
  });
}

function hardLeaveTicketShell(
  ticketId: string,
  queryClient: QueryClient,
): void {
  // Destination loader for another ticket may have already overwritten Redux.
  // Never wipe that hydration — only drop this ticket's comments cache.
  const current = (store.getState().ticket?.value ?? null) as {
    _id?: string;
  } | null;
  if (current?._id != null && String(current._id) !== ticketId) {
    dropTicketCommentsQuery(queryClient, ticketId);
    return;
  }

  store.dispatch(setLoading(false));
  store.dispatch(resetTicket());
  dropTicketCommentsQuery(queryClient, ticketId);
}

function finalizeTicketShellLeave(
  ticketId: string,
  queryClient: QueryClient,
  pathname: string,
): void {
  if (isStillOnSameTicketShell(ticketId, pathname)) {
    return;
  }
  hardLeaveTicketShell(ticketId, queryClient);
}

/**
 * Tear down the classic ticket Redux shell only when leaving the ticket
 * entirely (list, Ask Puntt, another ticket, etc.).
 *
 * TanStack Router's `onLeave` does not receive the destination match. Depending
 * on commit timing, `window.location` during `onLeave` can still reflect the
 * DEPARTURE path (always a ticket shell), which would make a sync soft-leave
 * check vacuously true and skip `resetTicket` forever. Deferred finalize reads
 * the destination after navigation commits; pass `nextPathname` to decide sync
 * (tests / callers that already know the destination).
 */
export function onTicketShellLeave(args: {
  ticketId: string;
  queryClient: QueryClient;
  nextPathname?: string;
}): void {
  const { ticketId, queryClient } = args;

  if (args.nextPathname != null) {
    finalizeTicketShellLeave(ticketId, queryClient, args.nextPathname);
    return;
  }

  const epoch = ++leaveEpoch;
  setTimeout(() => {
    if (epoch !== leaveEpoch) return;
    finalizeTicketShellLeave(ticketId, queryClient, readBrowserPathname());
  }, 0);
}

/** Ticket already hydrated for this id — skip loading spinner / hard remount. */
export function isTicketShellHydrated(ticketId: string): boolean {
  const current = (store.getState().ticket?.value ?? null) as {
    _id?: string;
  } | null;
  if (!current?._id) return false;
  return String(current._id) === ticketId;
}
